/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The single PUBLIC entry point for toasts. Everything in the app imports from here and
 * never from `sonner` directly.
 *
 * RGBARRY-CUSTOMIZATION (bundle-optimization): this module is deliberately sonner-free. Its
 * only static import is the type-only `./types`, so nothing here pulls `sonner` into a
 * page's eager modulepreload graph. The imperative API below lazy-loads the real
 * implementation (`./internal`, which owns the sonner import AND mounts the Toaster) via a
 * single memoized `import('./internal')` on first use. sonner therefore lands in a
 * dynamic-only chunk instead of shipping on every initial page load.
 */
import { type ReactNode } from 'react';
import type { ExternalToast, ToastOptions, ToastPromiseData, ToastType } from './types';
// Type-only imports (erased at build) — name the two forwarded exports' types without
// creating a static runtime edge to `./internal` (and thus sonner). Aliased to avoid
// colliding with this module's own `addToast`/`toast` exports.
import type { addToast as InternalAddToast, toast as InternalToast } from './internal';

export type { ToastType, ToastAction, ToastOptions } from './types';

/** Shape of the lazily-loaded implementation, limited to what the loader forwards to. */
type ToastInternal = { addToast: typeof InternalAddToast; toast: typeof InternalToast };

// Memoized so `./internal` (and thus sonner) is fetched at most once, on the first toast.
let internalPromise: Promise<ToastInternal> | undefined;
// Clear the cache on a failed load: a rejected import (e.g. a chunk-hash change after a deploy)
// must not be memoized, or every subsequent toast would be permanently dead for the session.
// Dropping the reference lets the next toast retry the import.
const load = (): Promise<ToastInternal> =>
    (internalPromise ??= import('./internal').catch((error) => {
        internalPromise = undefined;
        throw error;
    }));

// Fire-and-forget forwarder: loads the implementation and forwards, swallowing a failed load so
// it never surfaces as an unhandled rejection. Safe because nothing awaits a toast's result.
const forward = (call: (m: ToastInternal) => unknown): void => {
    void load()
        .then(call)
        .catch(() => undefined);
};

/**
 * Enqueue a toast. Maps the app's option shape onto sonner inside `./internal`, which also
 * mounts the Toaster on first use. Fire-and-forget — safe because nothing awaits the id.
 */
export function addToast(message: string, type: ToastType = 'info', options?: ToastOptions | number): void {
    forward((m) => m.addToast(message, type, options));
}

/** Hook form, kept identical to the previous surface so existing consumers are unchanged. */
export function useToast() {
    return { addToast };
}

/**
 * Imperative sonner-style API for the handful of direct callers. Each method lazy-loads the
 * implementation and forwards; `./internal` ensures the Toaster is mounted before publishing.
 */
export const toast = Object.assign(
    (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast(message, options)),
    {
        success: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.success(message, options)),
        error: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.error(message, options)),
        info: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.info(message, options)),
        warning: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.warning(message, options)),
        message: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.message(message, options)),
        loading: (message: ReactNode, options?: ExternalToast) => forward((m) => m.toast.loading(message, options)),
        dismiss: (id?: string | number) => {
            if (typeof document !== 'undefined' && !document.querySelector('[data-sonner-toaster]')) return;
            forward((m) => m.toast.dismiss(id));
        },
        promise: <T>(promise: Promise<T>, data?: ToastPromiseData<T>) => {
            void promise.catch(() => undefined);
            forward((m) => m.toast.promise(promise, data));
        },
    }
);
